"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { GREEN } from "@/lib/constants";

const RINGS = [0, 1, 2];

const PARTICLES = [
  { x: 110, delay: 0.0, duration: 5.2 },
  { x: 142, delay: 1.4, duration: 4.6 },
  { x: 168, delay: 2.6, duration: 5.6 },
  { x: 196, delay: 0.8, duration: 5.0 },
  { x: 224, delay: 3.2, duration: 4.8 },
  { x: 252, delay: 1.8, duration: 5.4 },
  { x: 286, delay: 0.4, duration: 5.0 },
  { x: 316, delay: 2.2, duration: 4.7 },
] as const;

const MODULES = [0, 1, 2];

export default function ProgramOverviewVisual() {
  const reduce = useReducedMotion();

  return (
    <div className="relative mx-auto aspect-square w-full max-w-md">
      <svg viewBox="0 0 400 400" className="h-full w-full" aria-hidden="true" role="presentation">
        <defs>
          <radialGradient id="po-bg-glow" cx="50%" cy="55%" r="50%">
            <stop offset="0%" stopColor={GREEN} stopOpacity="0.14" />
            <stop offset="50%" stopColor={GREEN} stopOpacity="0.05" />
            <stop offset="100%" stopColor={GREEN} stopOpacity="0" />
          </radialGradient>
          <linearGradient id="po-device-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--foreground)" stopOpacity="0.10" />
            <stop offset="100%" stopColor="var(--foreground)" stopOpacity="0.02" />
          </linearGradient>
          <pattern id="po-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path
              d="M 20 0 L 0 0 0 20"
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.08"
              strokeWidth="0.5"
            />
          </pattern>
          <radialGradient id="po-mask" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <mask id="po-grid-mask">
            <rect width="400" height="400" fill="url(#po-mask)" />
          </mask>
        </defs>

        <g mask="url(#po-grid-mask)" style={{ color: "var(--foreground)" }}>
          <rect width="400" height="400" fill="url(#po-grid)" />
        </g>

        <ellipse cx="200" cy="220" rx="190" ry="170" fill="url(#po-bg-glow)" />

        {RINGS.map((i) => (
          <motion.circle
            key={`ring-${i}`}
            cx="200"
            cy="205"
            r="58"
            fill="none"
            stroke={GREEN}
            strokeOpacity="0.45"
            strokeWidth="1"
            initial={{ scale: 0.7, opacity: 0 }}
            animate={
              reduce ? { scale: 1, opacity: 0.2 } : { scale: [0.7, 2.4], opacity: [0.55, 0] }
            }
            transition={
              reduce
                ? undefined
                : {
                    duration: 3.6,
                    repeat: Infinity,
                    delay: i * 1.2,
                    ease: "easeOut",
                  }
            }
            style={{ transformOrigin: "200px 205px" }}
          />
        ))}

        <line
          x1="100"
          y1="252"
          x2="300"
          y2="252"
          stroke="var(--border)"
          strokeOpacity="0.6"
          strokeWidth="1"
          strokeDasharray="2 4"
        />

        <g>
          {MODULES.map((m) => {
            const y = 168 + m * 26;
            const ledY = y + 11;
            return (
              <g key={`mod-${m}`}>
                <rect
                  x="138"
                  y={y}
                  width="124"
                  height="22"
                  rx="4"
                  fill="url(#po-device-grad)"
                  stroke="var(--border)"
                  strokeWidth="1"
                />
                <motion.circle
                  cx="150"
                  cy={ledY}
                  r="2.5"
                  fill={GREEN}
                  initial={{ opacity: 1 }}
                  animate={reduce ? { opacity: 1 } : { opacity: [1, 0.25, 1] }}
                  transition={
                    reduce
                      ? undefined
                      : {
                          duration: 1.6,
                          repeat: Infinity,
                          delay: m * 0.4,
                          ease: "easeInOut",
                        }
                  }
                />
                <line
                  x1="166"
                  y1={ledY}
                  x2="248"
                  y2={ledY}
                  stroke="var(--border)"
                  strokeOpacity="0.5"
                  strokeWidth="0.5"
                  strokeDasharray="2 2"
                />
                <rect
                  x="252"
                  y={y + 8}
                  width="6"
                  height="6"
                  rx="1"
                  fill="var(--foreground)"
                  fillOpacity="0.08"
                  stroke="var(--border)"
                  strokeWidth="0.5"
                />
              </g>
            );
          })}
        </g>

        {!reduce &&
          PARTICLES.map((p) => (
            <motion.circle
              key={`particle-${p.x}-${p.delay}`}
              cx={p.x}
              r="1.5"
              fill={GREEN}
              fillOpacity="0.7"
              initial={{ cy: 360, opacity: 0 }}
              animate={{
                cy: [360, 60],
                opacity: [0, 0.7, 0.7, 0],
              }}
              transition={{
                duration: p.duration,
                repeat: Infinity,
                delay: p.delay,
                ease: "linear",
                times: [0, 0.15, 0.85, 1],
              }}
            />
          ))}
      </svg>
    </div>
  );
}
